import { useEffect, useRef, useState } from "react";
import { IMAGES } from "@/lib/site";

const SEEN_KEY = "vd_splash_seen";
const MAX_DURATION = 6500; // safety: never block the site longer than this

export default function SplashScreen() {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem(SEEN_KEY);
  });
  const [leaving, setLeaving] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef(null);

  const dismiss = () => {
    setLeaving(true);
    sessionStorage.setItem(SEEN_KEY, "1");
    setTimeout(() => setShow(false), 550);
  };

  useEffect(() => {
    if (!show) return;
    document.body.style.overflow = "hidden";
    const safety = setTimeout(dismiss, MAX_DURATION);
    // If the video can't start within 1.2s, show the static fallback briefly.
    const startCheck = setTimeout(() => {
      const v = videoRef.current;
      if (!v || v.readyState < 2) setVideoFailed(true);
    }, 1200);
    return () => {
      document.body.style.overflow = "";
      clearTimeout(safety);
      clearTimeout(startCheck);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // When falling back to the static logo, auto-dismiss after a short beat.
  useEffect(() => {
    if (videoFailed && show && !leaving) {
      const t = setTimeout(dismiss, 1400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoFailed, show, leaving]);

  if (!show) return null;

  return (
    <div
      data-testid="splash-screen"
      onClick={dismiss}
      className={`fixed inset-0 z-[100] grid place-items-center bg-[#FAFAFA] transition-opacity duration-500 ${
        leaving ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
    >
      {!videoFailed ? (
        <video
          ref={videoRef}
          data-testid="splash-video"
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={dismiss}
          onError={() => setVideoFailed(true)}
          className="w-56 h-56 sm:w-72 sm:h-72 md:w-80 md:h-80 object-contain [mix-blend-mode:multiply]"
        >
          <source src="/brand/vd-animated.webm" type="video/webm" />
          <source src="/brand/vd-animated.mp4" type="video/mp4" />
        </video>
      ) : (
        <img
          src={IMAGES.logo}
          alt="Vinodha Diagnostics"
          className="w-56 sm:w-72 h-auto animate-pulse"
        />
      )}
    </div>
  );
}
